import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import reactStringReplace from 'react-string-replace';
import sanitizeHtml from 'sanitize-html-react';

const MAX_CHAR_LIMIT = 1500;
const MAX_ATTACHMENTS = 3;

const TEMPLATE_OPTIONS = [
  { value: ':name:', label: 'Client Name' },
  { value: ':associate:', label: 'Associate Name' },
  { value: ':assigned_associate_name:', label: 'Associate Name' },
  { value: ':store:', label: 'Store Name' },
  { value: ':merchant:', label: 'Merchant Name' },
  { value: ':appointment_date:', label: 'Appointment Date' },
  { value: ':appointment_time:', label: 'Appointment Time' },
  { value: ':review_link:', label: 'Review Link' },
];

const TEMPLATE_BUTTONS = [
  { value: ':name:', label: 'Client Name' },
  { value: ':appointment_date:', label: 'Appointment Date' },
  { value: ':appointment_time:', label: 'Appointment Time' },
  { value: ':store:', label: 'Store Name' },
  { value: ':associate:', label: 'Associate Name' },
];

const buttonStyles = {
  verticalAlign: 'middle',
  border: '1px solid #dedede',
  borderRadius: '4px',
  padding: '7px 13px 7px 11px',
  backgroundColor: '#f3f3f3',
  color: '#818181',
  cursor: 'pointer',
  fontSize: '10px',
};

const hoverButtonStyles = {
  ...buttonStyles,
  backgroundColor: '#d3d3d3',
  color: '#3d3d3d',
};

const parseToHtml = value => {
  const regex = new RegExp(`(${TEMPLATE_OPTIONS.map(t => t.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`);
  const splitString = reactStringReplace(value, regex, (match, i) => {
    const find = TEMPLATE_OPTIONS.find(t => t.value === match);
    if (find) {
      return `<span class="ant-tag-clientbook" contentEditable="false" data-replacement="${find.value}">${find.label}</span>`;
    }
    return match;
  });
  return splitString.join('');
};

const parseToString = value => {
  let splitString = reactStringReplace(value, /<span[^>]*data-replacement="([^"]*)"[^>]*>[^<]*<\/span>/g, (match, i) => {
    return match;
  });
  // Fallback: convert label-based spans
  splitString = reactStringReplace(splitString.join(''), /<span[^>]*>(.*?)<\/span>/g, (match, i) => {
    const find = TEMPLATE_OPTIONS.find(t => t.label === match);
    if (find) return find.value;
    return match;
  });
  // Clean divs → newlines
  splitString = reactStringReplace(splitString.join(''), /<div[^>]*>(.*?)<\/div>/g, (match, i) => {
    return `${match}\n`;
  });
  // Strip remaining tags
  splitString = sanitizeHtml(splitString.join(''), {
    allowedTags: [],
    allowedAttributes: [],
  });
  splitString = reactStringReplace(splitString, /<[^>]*>/, (match, i) => match);
  return splitString.join('');
};

const MessageComposer = ({ value, onChange, templateButtons, onAttach, attachments, footer }) => {
  const [hovering, setHovering] = useState('');
  const refEditor = useRef();
  const fileInputRef = useRef();
  const isMounted = useRef(false);
  const [defaultValue, setDefaultValue] = useState(parseToHtml(value || ''));

  const buttons = templateButtons || TEMPLATE_BUTTONS;

  useEffect(() => {
    if (!isMounted.current) {
      setDefaultValue(parseToHtml(value || ''));
    }
  }, [value]);

  const handleInput = useCallback(
    e => {
      isMounted.current = true;
      const text = parseToString(e.target.innerHTML);
      onChange(text);
    },
    [onChange],
  );

  const checkParentElement = (element, id) => {
    if (element?.id === id) return true;
    if (element?.parentElement) return checkParentElement(element.parentElement, id);
    return false;
  };

  const focusEditorEnd = () => {
    const editor = refEditor.current;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const insertTemplateVar = option => {
    const plainText = parseToString(refEditor.current?.innerHTML || '');
    if (plainText.length >= MAX_CHAR_LIMIT) return;

    let selection = window.getSelection();
    let range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;

    const isTextArea = checkParentElement(selection?.anchorNode, 'massMessageComposer');
    const isData = !!range?.commonAncestorContainer?.parentElement?.getAttribute('data-replacement');

    // If editor isn't focused, focus it and place cursor at end
    if (isData || !isTextArea) {
      focusEditorEnd();
      selection = window.getSelection();
      range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
      if (!range) return;
    }

    const myMetaTag = document.createElement('span');
    myMetaTag.setAttribute('data-replacement', option.value);
    myMetaTag.setAttribute('contenteditable', 'false');
    myMetaTag.setAttribute('class', 'ant-tag-clientbook');
    myMetaTag.innerText = option.label;

    const textElement = document.createTextNode('');
    range?.insertNode(myMetaTag);
    range?.collapse(false);
    range?.insertNode(textElement);
    range?.collapse(false);
    const newSelection = window.getSelection();
    newSelection?.removeAllRanges();
    newSelection?.addRange(range);

    isMounted.current = true;
    onChange(parseToString(range.commonAncestorContainer.innerHTML));
  };

  const handlePaste = e => {
    e.preventDefault();
    let paste = e.clipboardData.getData('text');
    paste = parseToHtml(paste);

    const selection = window.getSelection();
    const range = selection?.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range) return;

    const pasteNode = document.createTextNode(paste);
    const textElement = document.createTextNode('');
    range.insertNode(pasteNode);
    range.collapse(false);
    range.insertNode(textElement);
    range.collapse(false);
    const newSelection = window.getSelection();
    newSelection?.removeAllRanges();
    newSelection?.addRange(range);

    isMounted.current = true;
    onChange(parseToString(range.commonAncestorContainer.innerHTML));
  };

  const handleKeyDown = e => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak');
      return;
    }

    // Handle backspace/delete to remove template tags
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);

      // Check if cursor is collapsed (no text selected)
      if (!range.collapsed) return;

      const node = range.startContainer;
      const offset = range.startOffset;

      let tagToRemove = null;

      if (e.key === 'Backspace') {
        // Check previous sibling or previous node
        if (node.nodeType === Node.TEXT_NODE && offset === 0) {
          // Cursor at start of text node — check previous sibling
          const prev = node.previousSibling;
          if (prev && prev.getAttribute && prev.getAttribute('data-replacement')) {
            tagToRemove = prev;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
          const prev = node.childNodes[offset - 1];
          if (prev && prev.getAttribute && prev.getAttribute('data-replacement')) {
            tagToRemove = prev;
          }
        }
      } else {
        // Delete key
        if (node.nodeType === Node.TEXT_NODE && offset === node.textContent.length) {
          const next = node.nextSibling;
          if (next && next.getAttribute && next.getAttribute('data-replacement')) {
            tagToRemove = next;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE && offset < node.childNodes.length) {
          const next = node.childNodes[offset];
          if (next && next.getAttribute && next.getAttribute('data-replacement')) {
            tagToRemove = next;
          }
        }
      }

      if (tagToRemove) {
        e.preventDefault();
        tagToRemove.remove();
        isMounted.current = true;
        onChange(parseToString(refEditor.current.innerHTML));
      }
    }
  };

  const handleFileSelect = e => {
    const files = Array.from(e.target.files || []);
    if (files.length && onAttach) {
      const currentCount = (attachments || []).length;
      const allowed = MAX_ATTACHMENTS - currentCount;
      if (allowed <= 0) return;
      onAttach(files.slice(0, allowed));
    }
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const attachmentsEnabled = !!onAttach;
  const currentAttachments = attachments || [];
  const canAddMore = currentAttachments.length < MAX_ATTACHMENTS;

  return (
    <div
      style={{
        width: '100%',
        border: '1px #818181 solid',
        borderRadius: '3px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={{ position: 'relative', minHeight: '120px' }}>
          <div
            id="massMessageComposer"
            contentEditable
            onInput={handleInput}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              caretColor: 'black',
              display: 'block',
              fontFamily: 'Poppins',
              letterSpacing: 0.5,
              wordSpacing: 1,
              whiteSpace: 'pre-wrap',
              resize: 'none',
              minHeight: '120px',
              maxHeight: '250px',
              padding: '15px 15px 10px 15px',
              outline: 'none',
              textAlign: 'left',
              overflowY: 'auto',
            }}
            ref={refEditor}
            dangerouslySetInnerHTML={{ __html: defaultValue }}
            className="textarea-w-actions scrollbar-y"
            data-ph="Write a message..."
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>
      </div>

      {/* Attachment thumbnails */}
      {currentAttachments.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            padding: '8px 15px',
            flexWrap: 'wrap',
            borderTop: '1px solid #dedede',
          }}
        >
          {currentAttachments.map((att, idx) => (
            <div
              key={att.id || idx}
              style={{
                position: 'relative',
                width: '64px',
                height: '64px',
                borderRadius: '4px',
                overflow: 'hidden',
                border: '1px solid #dedede',
              }}
            >
              {att.uploading ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'spinner-third']} spin style={{ color: '#818181' }} />
                </div>
              ) : (
                <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                  <img
                    src={att.url}
                    alt={`Attachment ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </a>
              )}
              {att.onRemove && (
                <button
                  type="button"
                  onClick={att.onRemove}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    padding: 0,
                  }}
                >
                  <FontAwesomeIcon icon={['far', 'times']} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          width: '100%',
          minHeight: '43px',
          backgroundColor: '#f3f3f3',
          borderTop: '1px solid #dedede',
          borderRadius: '0 0 3px 3px',
          padding: '4px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {buttons.map(btn => (
            <button
              key={btn.value}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => insertTemplateVar(btn)}
              onMouseEnter={() => setHovering(btn.value)}
              onMouseLeave={() => setHovering('')}
              style={hovering === btn.value ? hoverButtonStyles : buttonStyles}
            >
              {btn.label}
            </button>
          ))}
        </div>
        {attachmentsEnabled ? (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div
              style={{
                width: '29px',
                height: '29px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '4px',
                flexShrink: 0,
                cursor: canAddMore ? 'pointer' : 'not-allowed',
                opacity: canAddMore ? 1 : 0.5,
              }}
              title={canAddMore ? 'Attach images (max 3)' : 'Maximum attachments reached'}
              onClick={() => canAddMore && fileInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={['far', 'paperclip']} style={{ color: '#818181', fontSize: '15px' }} />
            </div>
          </div>
        ) : (
          <div
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '4px',
              flexShrink: 0,
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
            title="Attachments coming soon"
          >
            <FontAwesomeIcon icon={['far', 'paperclip']} style={{ color: '#818181', fontSize: '15px' }} />
          </div>
        )}
      </div>
      {footer}
    </div>
  );
};

export { parseToHtml, parseToString, TEMPLATE_OPTIONS, TEMPLATE_BUTTONS };
export default MessageComposer;
